import {
  Cog,
  Zap,
  PowerOff,
  TriangleAlert,
  WifiOff,
  CheckCircle2,
  PackagePlus,
} from "lucide-react";
import { Handle, Position, type NodeProps } from "reactflow";
import { Pill, type PillTone } from "@/components/ui/pill";
import { cn } from "@/lib/utils";
import { NodeShell, StartNode, EndNode } from "@/features/lines/canvas/nodes";
import { edgeTypes } from "@/features/lines/canvas/edges";
import type { SandboxFlowNodeData, SandboxNodeState } from "@/types/sim-sandbox.types";
import { SandboxLaneNode } from "./SandboxLaneNode";

// Nó de máquina do sandbox sobre o NodeShell reusado do editor de linha. Cada estado
// tem cor + rótulo + ícone (nunca só cor). "sem comunicação" = Machine.status NODATA.
type MachineStatus = "RUNNING" | "STOPPED" | "IDLE" | "NODATA";

const STATE_META: Record<
  SandboxNodeState,
  { tone: PillTone; label: string; Icon: typeof Cog; status?: MachineStatus; pending?: boolean }
> = {
  normal: { tone: "success", label: "Ativa", Icon: CheckCircle2, status: "RUNNING" },
  bottleneck: { tone: "warning", label: "Gargalo", Icon: Zap, status: "IDLE" },
  off: { tone: "neutral", label: "Desligada", Icon: PowerOff, status: "STOPPED" },
  incompatible: { tone: "danger", label: "Incompatível", Icon: TriangleAlert, pending: true },
  added: { tone: "info", label: "Adicionada", Icon: PackagePlus, status: "RUNNING" },
  nodata: { tone: "neutral", label: "Sem comunicação", Icon: WifiOff, status: "NODATA" },
};

export function SandboxMachineNode({ data, selected }: NodeProps<SandboxFlowNodeData>) {
  const meta = STATE_META[data.state];
  const StateIcon = meta.Icon;
  return (
    <NodeShell
      selected={selected}
      status={meta.status}
      pending={meta.pending}
      className={cn("min-w-[168px]", data.state === "off" && "opacity-60")}
    >
      {/* Handles são âncora de edge, não só de conexão manual — sem eles o React Flow não
       * calcula handleBounds e as edges PRODUCTION_FLOW não renderizam (nodesConnectable=false
       * já bloqueia o drag-to-connect, então isConnectable=false aqui só remove o afford. residual).*/}
      <Handle type="target" position={Position.Left} isConnectable={false} />
      <div className="flex items-center gap-2 pt-0.5">
        <Cog className="h-4 w-4 shrink-0 text-text-tertiary" />
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-sm font-medium leading-tight">{data.label}</span>
          <span className="truncate font-mono text-[10px] text-text-tertiary">{data.code}</span>
        </div>
        {data.origin === "acervo" && data.state !== "added" ? (
          <span
            title="Máquina do acervo"
            aria-label="Máquina do acervo"
            className="text-[9px] font-semibold uppercase tracking-wide text-primary"
          >
            Acervo
          </span>
        ) : null}
      </div>
      <div className="mt-1.5 flex items-center gap-1.5">
        <Pill tone={meta.tone} withDot icon={<StateIcon className="h-3 w-3" />}>
          {meta.label}
        </Pill>
        {data.provenance === "measured" ? (
          <span
            title="Parâmetros medidos"
            aria-label="Parâmetros medidos"
            className="text-[9px] font-semibold uppercase tracking-wide text-rd-success-700"
          >
            Medido
          </span>
        ) : null}
      </div>
      <Handle type="source" position={Position.Right} isConnectable={false} />
    </NodeShell>
  );
}

// Consts de MÓDULO (React Flow v11 re-monta se recriados a cada render).
export const sandboxNodeTypes = {
  MACHINE: SandboxMachineNode,
  START: StartNode,
  END: EndNode,
  STAGE_LANE: SandboxLaneNode,
} as const;

export const sandboxEdgeTypes = { PRODUCTION_FLOW: edgeTypes.PRODUCTION_FLOW } as const;
